import { type IMarket, MarketStatus } from "~/types/event";
import type { Platform } from "~/types/platform";
import {
  getMarketSettlement,
  type IMarketSettlement,
  type MarketResult,
  MarketSettlementKind,
} from "~/utils/resolution/getMarketSettlement";

export enum OutcomeSettlementState {
  OPEN = "open",
  PARTIAL = "partial",
  DISPUTED = "disputed",
  CLOSED = "closed",
  RESOLVED = "resolved",
}

export enum OutcomeResult {
  YES = "yes",
  NO = "no",
  MIXED = "mixed",
}

type MarketSettlements = ReadonlyMap<string, IMarketSettlement>;

export interface IOutcomeSettlement {
  state: OutcomeSettlementState;
  result: OutcomeResult | null;
  markets: MarketSettlements;
  openPlatforms: Platform[];
  settledPlatforms: Platform[];
  disputedPlatforms: Platform[];
}

interface IOutcomeSettlementFlags {
  isResolved: boolean;
  isDisputed: boolean;
  isClosed: boolean;
  isSettled: boolean;
}

export interface IResultGroup {
  result: MarketResult;
  platforms: Platform[];
}

type SettledGroup =
  | IResultGroup
  | { result: null; platforms: Platform[] };

export const getOutcomeSettlementFlags = ({
  state,
}: IOutcomeSettlement): IOutcomeSettlementFlags => {
  const isResolved = state === OutcomeSettlementState.RESOLVED;
  const isDisputed = state === OutcomeSettlementState.DISPUTED;
  const isClosed = state === OutcomeSettlementState.CLOSED;

  return {
    isResolved,
    isDisputed,
    isClosed,
    isSettled: isResolved || isDisputed || isClosed,
  };
};

export const marketsOfKind = (
  markets: readonly IMarket[],
  settlements: MarketSettlements,
  kind: MarketSettlementKind
): IMarket[] =>
  markets.filter(({ id }) => settlements.get(id)?.kind === kind);

// The settled markets' platforms by what they paid — Yes, then No, then the
// ones without a stored result; empty groups left out.
export const groupSettledByResult = (
  markets: readonly IMarket[],
  settlements: MarketSettlements
): SettledGroup[] => {
  const settled = marketsOfKind(
    markets,
    settlements,
    MarketSettlementKind.SETTLED
  );
  const results: (MarketResult | null)[] = ["yes", "no", null];

  return results
    .map((result) => ({
      result,
      platforms: settled
        .filter(({ id }) => settlements.get(id)?.result === result)
        .map(({ platform }) => platform),
    }))
    .filter(({ platforms }) => platforms.length > 0);
};

export const hasResult = (group: SettledGroup): group is IResultGroup =>
  group.result !== null;

const getState = (
  open: number,
  settled: number,
  disputed: number,
  hasResolved: boolean
): OutcomeSettlementState => {
  if (open > 0) {
    return settled + disputed > 0
      ? OutcomeSettlementState.PARTIAL
      : OutcomeSettlementState.OPEN;
  }

  if (disputed > 0) {
    return OutcomeSettlementState.DISPUTED;
  }

  if (settled === 0) {
    return OutcomeSettlementState.OPEN;
  }

  return hasResolved
    ? OutcomeSettlementState.RESOLVED
    : OutcomeSettlementState.CLOSED;
};

const getResult = (settlements: MarketSettlements): OutcomeResult | null => {
  const results = new Set(
    [...settlements.values()]
      .map(({ result }) => result)
      .filter((result) => result !== null)
  );

  if (results.size > 1) {
    return OutcomeResult.MIXED;
  }

  if (results.has("yes")) {
    return OutcomeResult.YES;
  }

  return results.has("no") ? OutcomeResult.NO : null;
};

export const getOutcomeSettlement = (
  markets: readonly IMarket[]
): IOutcomeSettlement => {
  const settlements = new Map(
    markets.map((market) => [market.id, getMarketSettlement(market)])
  );
  const platformsOf = (kind: MarketSettlementKind): Platform[] =>
    marketsOfKind(markets, settlements, kind).map(({ platform }) => platform);
  const openPlatforms = platformsOf(MarketSettlementKind.OPEN);
  const settledPlatforms = platformsOf(MarketSettlementKind.SETTLED);
  const disputedPlatforms = platformsOf(MarketSettlementKind.DISPUTED);
  const result = getResult(settlements);
  const hasResolvedMarket = markets.some(
    ({ unifiedStatus }) => unifiedStatus === MarketStatus.RESOLVED
  );

  return {
    state: getState(
      openPlatforms.length,
      settledPlatforms.length,
      disputedPlatforms.length,
      hasResolvedMarket
    ),
    result,
    markets: settlements,
    openPlatforms,
    settledPlatforms,
    disputedPlatforms,
  };
};
